import React, { useEffect, useMemo, useState } from "react";
import LoadingSpinner from "../../common/LoadingSpinner";
import { permissionsAPI, projectsAPI, usersAPI } from "../../../services/api";
import {
  SYSTEM_ROLES,
  PROJECT_ROLES,
  EMPTY_NEW_USER,
  BACKEND_PROJECT_PERMISSION_SCHEMA,
  DEFAULT_PROJECT_ROLE_PERMISSIONS,
} from "../../../constants/userManagement";
import { isSystemAdmin } from "../../../constants/systemRoles";

const norm = (v) => (v ?? "").toString();

const mergePermMaps = (base, extra) => {
  const out = structuredClone(base || {});
  const e = extra || {};
  Object.entries(e).forEach(([resource, actions]) => {
    const cur = new Set(out[resource] || []);
    (actions || []).forEach((a) => cur.add(a));
    out[resource] = Array.from(cur);
  });
  return out;
};

const buildDefaultPermsForRole = (role) => {
  if (DEFAULT_PROJECT_ROLE_PERMISSIONS[role] === "ALL") {
    const all = {};
    Object.entries(BACKEND_PROJECT_PERMISSION_SCHEMA).forEach(([r, acts]) => {
      all[r] = [...acts];
    });
    return all;
  }
  return DEFAULT_PROJECT_ROLE_PERMISSIONS[role] || {};
};
const sanitizeProjectPerms = (draft) => {
  const out = {};
  const d = draft || {};
  Object.entries(BACKEND_PROJECT_PERMISSION_SCHEMA).forEach(
    ([resource, allowedActions]) => {
      const allowed = new Set(allowedActions);
      const current = Array.isArray(d[resource]) ? d[resource] : [];
      const cleaned = current
        .map((x) => norm(x).trim())
        .filter((x) => allowed.has(x))
        .sort();
      if (cleaned.length) out[resource] = cleaned;
    },
  );
  return out;
};

export default function UsersView() {
  const [me, setMe] = useState(null);
  const [meLoading, setMeLoading] = useState(true);

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState(null);

  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);

  const [newUser, setNewUser] = useState({ ...EMPTY_NEW_USER });
  const [creating, setCreating] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [savingUser, setSavingUser] = useState(false);

  const [projectAssign, setProjectAssign] = useState({
    user_id: "",
    project_id: "",
    role: "inspector",
  });
  const [assigning, setAssigning] = useState(false);

  const [permRoles, setPermRoles] = useState([]);
  const [permLoading, setPermLoading] = useState(false);
  const [permError, setPermError] = useState(null);
  const [activePermRole, setActivePermRole] = useState("inspector");
  const [permDraft, setPermDraft] = useState({});
  const [savingPerms, setSavingPerms] = useState(false);
  const [resettingPerms, setResettingPerms] = useState(false);

  const canManageUsers = useMemo(() => isSystemAdmin(me?.role), [me]);

  const loadMe = async () => {
    setMeLoading(true);
    try {
      const res = await usersAPI.getCurrent();
      setMe(res.data || null);
    } finally {
      setMeLoading(false);
    }
  };

  const loadUsers = async () => {
    setLoadingUsers(true);
    setUsersError(null);
    try {
      const res = await usersAPI.list();
      setUsers(res.data || []);
    } catch (e) {
      setUsersError(e);
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadProjects = async () => {
    setLoadingProjects(true);
    try {
      const res = await projectsAPI.getAll();
      setProjects(res.data || []);
    } finally {
      setLoadingProjects(false);
    }
  };

  const loadPermissions = async () => {
    setPermLoading(true);
    setPermError(null);
    try {
      const res = await permissionsAPI.listProjectRolePermissions();
      const list = res.data || [];
      setPermRoles(list);

      const first = list.find((x) => x.role === activePermRole) || list[0];
      if (first?.role) {
        setActivePermRole(first.role);

        const base = buildDefaultPermsForRole(first.role);
        const merged = mergePermMaps(base, first.permissions || {});
        setPermDraft(sanitizeProjectPerms(merged));
      } else {
        setPermDraft({});
      }
    } catch (e) {
      setPermError(e);
    } finally {
      setPermLoading(false);
    }
  };

  useEffect(() => {
    loadMe();
  }, []);

  useEffect(() => {
    if (!me) return;
    if (!canManageUsers) return;
    loadUsers();
    loadProjects();
    loadPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me, canManageUsers]);

  const onCreateUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await usersAPI.create({
        ...newUser,
        email: newUser.email.trim(),
        first_name: newUser.first_name.trim(),
        last_name: newUser.last_name.trim(),
      });
      setNewUser({ ...EMPTY_NEW_USER });
      await loadUsers();
    } catch (err) {
      alert(
        err?.response?.data?.detail || err?.message || "Failed to create user",
      );
    } finally {
      setCreating(false);
    }
  };

  const onUpdateSystemRole = async (userId, role) => {
    try {
      await usersAPI.updateSystemRole(userId, { role });
      await loadUsers();
    } catch (err) {
      alert(
        err?.response?.data?.detail || err?.message || "Failed to update role",
      );
    }
  };

  const openEditUser = (user) => {
    setEditingUser(user);
    setEditDraft({
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      email: user.email || "",
      phone: user.phone || "",
      company: user.company || "",
      role: user.role || "user_internal",
      cognito_sub: user.cognito_sub || "",
      is_active: user.is_active !== false,
    });
  };

  const saveEditedUser = async (e) => {
    e?.preventDefault?.();
    if (!editingUser?.id || !editDraft) return;
    setSavingUser(true);
    try {
      await usersAPI.update(editingUser.id, {
        ...editDraft,
        first_name: editDraft.first_name.trim(),
        last_name: editDraft.last_name.trim(),
        email: editDraft.email.trim(),
        phone: editDraft.phone.trim() || null,
        company: editDraft.company.trim() || null,
        cognito_sub: editDraft.cognito_sub.trim(),
      });
      setEditingUser(null);
      setEditDraft(null);
      await loadUsers();
    } catch (err) {
      alert(err?.response?.data?.detail || err?.message || "Failed to update user");
    } finally {
      setSavingUser(false);
    }
  };

  const onDeactivateUser = async (userId) => {
    if (
      !window.confirm(
        "Deactivate this user? (They will not be able to use the app)",
      )
    )
      return;
    try {
      await usersAPI.deactivate(userId);
      await loadUsers();
    } catch (err) {
      alert(
        err?.response?.data?.detail ||
          err?.message ||
          "Failed to deactivate user",
      );
    }
  };

  const onAssignProjectRole = async (e) => {
    e.preventDefault();
    const projectId = Number(projectAssign.project_id);
    const userId = Number(projectAssign.user_id);
    if (!projectId || !userId) {
      alert("Select both user and project");
      return;
    }
    setAssigning(true);
    try {
      await projectsAPI.addTeamMember(projectId, {
        user_id: userId,
        role: projectAssign.role,
      });
      alert("Project role assigned");
    } catch (err) {
      alert(
        err?.response?.data?.detail ||
          err?.message ||
          "Failed to assign project role",
      );
    } finally {
      setAssigning(false);
    }
  };

  const allResources = useMemo(
    () => Object.keys(BACKEND_PROJECT_PERMISSION_SCHEMA).sort(),
    [],
  );

  const actionsByResource = useMemo(() => {
    const out = {};
    Object.entries(BACKEND_PROJECT_PERMISSION_SCHEMA).forEach(([k, v]) => {
      out[k] = [...v].sort();
    });
    return out;
  }, []);

  const onSelectPermRole = (role) => {
    setActivePermRole(role);
    const found = (permRoles || []).find((x) => x.role === role);

    const base = buildDefaultPermsForRole(role);
    const merged = mergePermMaps(base, found?.permissions || {});
    setPermDraft(sanitizeProjectPerms(merged));
  };

  const togglePerm = (resource, action) => {
    setPermDraft((prev) => {
      const next = structuredClone(prev || {});
      const allowed = new Set(
        BACKEND_PROJECT_PERMISSION_SCHEMA[resource] || [],
      );
      if (!allowed.has(action)) return next;

      const list = Array.isArray(next[resource]) ? [...next[resource]] : [];
      const i = list.indexOf(action);
      if (i >= 0) list.splice(i, 1);
      else list.push(action);

      const cleaned = list
        .filter(Boolean)
        .map((x) => norm(x).trim())
        .filter((x) => allowed.has(x))
        .sort();

      if (cleaned.length === 0) delete next[resource];
      else next[resource] = cleaned;

      return next;
    });
  };

  const savePerms = async () => {
    setSavingPerms(true);
    try {
      await permissionsAPI.upsertProjectRolePermissions(activePermRole, {
        permissions: sanitizeProjectPerms(permDraft || {}),
      });
      await loadPermissions();
      alert("Permissions saved");
    } catch (err) {
      alert(
        err?.response?.data?.detail ||
          err?.message ||
          "Failed to save permissions",
      );
    } finally {
      setSavingPerms(false);
    }
  };

  const resetPerms = async () => {
    if (!window.confirm("Reset this role's permissions to defaults?")) return;
    setResettingPerms(true);
    try {
      await permissionsAPI.resetProjectRolePermissions(activePermRole);
      await loadPermissions();
      alert("Permissions reset");
    } catch (err) {
      alert(
        err?.response?.data?.detail ||
          err?.message ||
          "Failed to reset permissions",
      );
    } finally {
      setResettingPerms(false);
    }
  };

  if (meLoading) return <LoadingSpinner />;

  if (!canManageUsers) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold">User Management</h1>
        <p className="mt-2 text-gray-600">
          You don't have permission to view this page.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8">
      <div>
        <h1 className="text-2xl font-semibold">User Management</h1>
        <p className="text-sm text-gray-600 mt-1">
          Create/deactivate users, assign system + project roles, and manage
          project-role permissions.
        </p>
      </div>

      {/* Create user */}
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold">Create user</h2>
        <p className="text-sm text-gray-500 mt-1">
          Note: this creates a user record in DB.
        </p>

        <form
          onSubmit={onCreateUser}
          className="mt-4 grid grid-cols-1 md:grid-cols-6 gap-3"
        >
          <input
            className="border rounded px-3 py-2 text-sm"
            placeholder="First name"
            value={newUser.first_name}
            onChange={(e) =>
              setNewUser((p) => ({ ...p, first_name: e.target.value }))
            }
            required
          />
          <input
            className="border rounded px-3 py-2 text-sm"
            placeholder="Last name"
            value={newUser.last_name}
            onChange={(e) =>
              setNewUser((p) => ({ ...p, last_name: e.target.value }))
            }
            required
          />
          <input
            className="border rounded px-3 py-2 text-sm md:col-span-2"
            placeholder="Email"
            value={newUser.email}
            onChange={(e) =>
              setNewUser((p) => ({ ...p, email: e.target.value }))
            }
            required
            type="email"
          />
          <select
            className="border rounded px-3 py-2 text-sm"
            value={newUser.role}
            onChange={(e) =>
              setNewUser((p) => ({ ...p, role: e.target.value }))
            }
          >
            {SYSTEM_ROLES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
          <button
            type="submit"
            disabled={creating}
            className="bg-black text-white rounded px-4 py-2 text-sm disabled:opacity-50"
          >
            {creating ? "Creating..." : "Create"}
          </button>

          <input
            className="border rounded px-3 py-2 text-sm"
            placeholder="Phone (optional)"
            value={newUser.phone}
            onChange={(e) =>
              setNewUser((p) => ({ ...p, phone: e.target.value }))
            }
          />
          <input
            className="border rounded px-3 py-2 text-sm md:col-span-2"
            placeholder="Company (optional)"
            value={newUser.company}
            onChange={(e) =>
              setNewUser((p) => ({ ...p, company: e.target.value }))
            }
          />
          <input
            className="border rounded px-3 py-2 text-sm md:col-span-2"
            placeholder="Cognito ID"
            value={newUser.cognito_sub}
            onChange={(e) =>
              setNewUser((p) => ({ ...p, cognito_sub: e.target.value }))
            }
          />

          <div className="md:col-span-3 flex items-center gap-2">
            <input
              id="newUserActive"
              type="checkbox"
              checked={!!newUser.is_active}
              onChange={(e) =>
                setNewUser((p) => ({ ...p, is_active: e.target.checked }))
              }
            />
            <label htmlFor="newUserActive" className="text-sm text-gray-700">
              Active
            </label>
          </div>
        </form>
      </section>

      {/* Assign project role */}
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold">Assign project role</h2>
        <form
          onSubmit={onAssignProjectRole}
          className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3"
        >
          <select
            className="border rounded px-3 py-2 text-sm"
            value={projectAssign.user_id}
            onChange={(e) =>
              setProjectAssign((p) => ({ ...p, user_id: e.target.value }))
            }
          >
            <option value="">Select user</option>
            {users
              .filter((u) => u?.is_active)
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.first_name} {u.last_name} — {u.email}
                </option>
              ))}
          </select>

          <select
            className="border rounded px-3 py-2 text-sm"
            value={projectAssign.project_id}
            onChange={(e) =>
              setProjectAssign((p) => ({ ...p, project_id: e.target.value }))
            }
            disabled={loadingProjects}
          >
            <option value="">Select project</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.project_number ? `${p.project_number} — ` : ""}
                {p.name}
              </option>
            ))}
          </select>

          <select
            className="border rounded px-3 py-2 text-sm"
            value={projectAssign.role}
            onChange={(e) =>
              setProjectAssign((p) => ({ ...p, role: e.target.value }))
            }
          >
            {PROJECT_ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>

          <button
            type="submit"
            disabled={assigning}
            className="bg-black text-white rounded px-4 py-2 text-sm disabled:opacity-50"
          >
            {assigning ? "Assigning..." : "Assign"}
          </button>
        </form>
      </section>

      {/* Users table */}
      <section className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Users</h2>
          <button
            onClick={loadUsers}
            className="border rounded px-3 py-2 text-sm"
            disabled={loadingUsers}
          >
            Refresh
          </button>
        </div>

        {usersError && (
          <div className="mt-3 text-sm text-red-600">
            {usersError?.response?.data?.detail ||
              usersError?.message ||
              "Failed to load users"}
          </div>
        )}

        {loadingUsers ? (
          <div className="mt-4">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-600 border-b">
                  <th className="py-2 pr-4">Name</th>
                  <th className="py-2 pr-4">Email</th>
                  <th className="py-2 pr-4">Company</th>
                  <th className="py-2 pr-4">Cognito Sub</th>
                  <th className="py-2 pr-4">System role</th>
                  <th className="py-2 pr-4">Active</th>
                  <th className="py-2 pr-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(users || []).map((u) => (
                  <tr key={u.id} className="border-b last:border-b-0">
                    <td className="py-2 pr-4 whitespace-nowrap">
                      {u.first_name} {u.last_name}
                    </td>
                    <td className="py-2 pr-4 whitespace-nowrap">{u.email}</td>
                    <td className="py-2 pr-4">{u.company || "-"}</td>
                    <td className="max-w-[220px] truncate py-2 pr-4 font-mono text-xs" title={u.cognito_sub || ""}>{u.cognito_sub || "-"}</td>
                    <td className="py-2 pr-4">
                      <select
                        className="border rounded px-2 py-1 text-sm"
                        value={u.role}
                        onChange={(e) =>
                          onUpdateSystemRole(u.id, e.target.value)
                        }
                      >
                        {SYSTEM_ROLES.map((r) => (
                          <option key={r.value} value={r.value}>
                            {r.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 pr-4">{u.is_active ? "Yes" : "No"}</td>
                    <td className="py-2 pr-4">
                      <div className="flex items-center gap-3">
                        <button
                          className="text-blue-600 hover:underline"
                          onClick={() => openEditUser(u)}
                        >
                          Edit
                        </button>
                        <button
                          className="text-red-600 hover:underline disabled:opacity-50"
                          disabled={!u.is_active}
                          onClick={() => onDeactivateUser(u.id)}
                        >
                          Deactivate
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {editingUser && editDraft ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4">
          <form onSubmit={saveEditedUser} className="max-h-[calc(100vh-2rem)] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-xl">
            <div className="flex items-start justify-between gap-3 border-b p-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Edit user</h3>
                <p className="text-xs text-gray-500">Update the database user profile and Cognito identity mapping.</p>
              </div>
              <button type="button" onClick={() => { setEditingUser(null); setEditDraft(null); }} className="rounded-lg border px-3 py-1.5 text-sm">Close</button>
            </div>
            <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-2">
              <input required className="border rounded px-3 py-2 text-sm" placeholder="First name" value={editDraft.first_name} onChange={(e) => setEditDraft((p) => ({ ...p, first_name: e.target.value }))} />
              <input required className="border rounded px-3 py-2 text-sm" placeholder="Last name" value={editDraft.last_name} onChange={(e) => setEditDraft((p) => ({ ...p, last_name: e.target.value }))} />
              <input required type="email" className="border rounded px-3 py-2 text-sm md:col-span-2" placeholder="Email" value={editDraft.email} onChange={(e) => setEditDraft((p) => ({ ...p, email: e.target.value }))} />
              <input className="border rounded px-3 py-2 text-sm" placeholder="Phone" value={editDraft.phone} onChange={(e) => setEditDraft((p) => ({ ...p, phone: e.target.value }))} />
              <input className="border rounded px-3 py-2 text-sm" placeholder="Company" value={editDraft.company} onChange={(e) => setEditDraft((p) => ({ ...p, company: e.target.value }))} />
              <select className="border rounded px-3 py-2 text-sm" value={editDraft.role} onChange={(e) => setEditDraft((p) => ({ ...p, role: e.target.value }))}>
                {SYSTEM_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
              <label className="flex items-center gap-2 rounded border px-3 py-2 text-sm">
                <input type="checkbox" checked={!!editDraft.is_active} onChange={(e) => setEditDraft((p) => ({ ...p, is_active: e.target.checked }))} /> Active
              </label>
              <div className="md:col-span-2">
                <label className="mb-1 block text-xs font-medium text-gray-600">Cognito Sub</label>
                <input required className="w-full border rounded px-3 py-2 font-mono text-sm" value={editDraft.cognito_sub} onChange={(e) => setEditDraft((p) => ({ ...p, cognito_sub: e.target.value }))} />
                <p className="mt-1 text-xs text-amber-700">Changing this changes the DB identity mapping. Use the actual Cognito user sub value.</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t p-4">
              <button type="button" onClick={() => { setEditingUser(null); setEditDraft(null); }} className="rounded-lg border px-4 py-2 text-sm">Cancel</button>
              <button type="submit" disabled={savingUser} className="rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{savingUser ? "Saving..." : "Save changes"}</button>
            </div>
          </form>
        </div>
      ) : null}

      {/* Permission editor */}
      <section className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Project-role permissions</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={loadPermissions}
              className="border rounded px-3 py-2 text-sm"
              disabled={permLoading}
            >
              Refresh
            </button>
            <button
              onClick={resetPerms}
              className="border rounded px-3 py-2 text-sm"
              disabled={resettingPerms || !activePermRole}
            >
              {resettingPerms ? "Resetting..." : "Reset to default"}
            </button>
            <button
              onClick={savePerms}
              className="bg-black text-white rounded px-3 py-2 text-sm disabled:opacity-50"
              disabled={savingPerms || !activePermRole}
            >
              {savingPerms ? "Saving..." : "Save"}
            </button>
          </div>
        </div>

        {permError && (
          <div className="mt-3 text-sm text-red-600">
            {permError?.response?.data?.detail ||
              permError?.message ||
              "Failed to load permissions"}
          </div>
        )}

        <div className="mt-4 flex flex-col md:flex-row gap-4">
          <div className="md:w-64">
            <label className="text-sm text-gray-700">Role</label>
            <select
              className="mt-1 w-full border rounded px-3 py-2 text-sm"
              value={activePermRole}
              onChange={(e) => onSelectPermRole(e.target.value)}
              disabled={permLoading}
            >
              {(permRoles || []).map((r) => (
                <option key={r.role} value={r.role}>
                  {r.role}
                </option>
              ))}
            </select>
            <p className="mt-2 text-xs text-gray-500">
              This controls what a <b>project role</b> can do. System admin
              always bypasses.
            </p>
          </div>

          <div className="flex-1 overflow-x-auto">
            {permLoading ? (
              <LoadingSpinner />
            ) : (
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-600 border-b">
                    <th className="py-2 pr-4">Resource</th>
                    <th className="py-2 pr-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allResources.map((resource) => {
                    const actions = actionsByResource[resource] || [];
                    const enabled = new Set(permDraft?.[resource] || []);
                    return (
                      <tr
                        key={resource}
                        className="border-b last:border-b-0 align-top"
                      >
                        <td className="py-3 pr-4 font-medium whitespace-nowrap">
                          {resource}
                        </td>
                        <td className="py-3 pr-4">
                          <div className="flex flex-wrap gap-3">
                            {actions.map((a) => (
                              <label
                                key={`${resource}.${a}`}
                                className="flex items-center gap-2"
                              >
                                <input
                                  type="checkbox"
                                  checked={enabled.has(a)}
                                  onChange={() => togglePerm(resource, a)}
                                />
                                <span className="text-sm">{a}</span>
                              </label>
                            ))}
                            {actions.length === 0 && (
                              <span className="text-xs text-gray-500">
                                No actions discovered
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
